import * as React from 'react';
import { cn } from '../../utils/cn';
import { resolvePane } from './brand-pane';
import { useScrollOverflow } from './use-scroll-overflow';
import { ScrollHint } from './scroll-hint';

/**
 * Card geometry of the two-pane surface: 977×635 against the 1920 canvas,
 * full-bleed below `md`.
 *
 * Scale steps, not arbitrary lengths — each app's Tailwind config maps the
 * numeric scale to its own root (web's is fluid, Business Manager's legacy rem
 * is about a pixel), and an arbitrary `[61.0625rem]` bypasses that mapping.
 * 158.75 = 635px, 244 = 976px.
 *
 * Exported so a surface rendered outside a dialog — the `/login` page — is the
 * same size as the modal without restating the numbers.
 */
export const TWO_PANE_SURFACE_SIZE_CLASSNAME = 'md:h-158.75 md:max-w-244';

export interface TwoPaneSurfaceProps {
    /**
     * Left pane: brand imagery, a checklist, a selection — whatever the surface
     * is about. Hidden below `md`, where the reader has no room for imagery
     * beside the thing they came to do, so anything they must not lose belongs
     * in `children` as well.
     *
     * When set, wins over `paneImage` / `paneStatement`.
     */
    pane?: React.ReactNode;
    /** Cover photo for the built-in brand pane. Used only when `pane` is omitted. */
    paneImage?: string;
    /** White statement at the bottom of the brand photo. Needs `paneImage`. */
    paneStatement?: string;
    /** Right pane: the form, steps, or whatever the reader acts on. */
    children: React.ReactNode;
    /**
     * Vertical padding of the right pane. `roomy` suits a single centred step,
     * and only means anything where the surface has a fixed height to centre
     * within.
     */
    bodyPadding?: 'default' | 'roomy';
    /** Inset pane corner radius. Matches the card the surface sits in. */
    corners?: 'square' | 'rounded';
    /**
     * Lifts the scroll hint off the bottom edge, for a step that pins its own
     * action there. Without it the hint sits behind that bar, which is opaque.
     */
    scrollHintClassName?: string;
    /**
     * Step key of a multi-step surface. Each change rewinds the scroll and
     * re-arms the scroll hint — see {@link useScrollOverflow}.
     */
    scrollResetKey?: string;
    paneClassName?: string;
    bodyClassName?: string;
}

/**
 * The two-pane layout itself: full-bleed left pane beside a scrollable right
 * pane, stacking below `md`.
 *
 * Deliberately free of any dialog: `TwoPaneModal` renders it inside a Dialog or
 * Drawer, and the `/login` page renders it as a plain region. Both get the same
 * panes at the same sizes because it is the same component, which is the whole
 * reason it is separate from the modal that usually holds it.
 */
export const TwoPaneSurface = ({
    pane,
    paneImage,
    paneStatement,
    children,
    bodyPadding = 'default',
    corners = 'square',
    scrollHintClassName,
    scrollResetKey,
    paneClassName,
    bodyClassName,
}: TwoPaneSurfaceProps) => {
    const resolvedPane = resolvePane(pane, paneImage, paneStatement);
    const rounded = corners === 'rounded';
    const scroll = useScrollOverflow<HTMLDivElement>(scrollResetKey);

    return (
        <div className="flex h-full max-md:flex-col">
            {resolvedPane ? (
                <div
                    className={cn(
                        // 119.5 = 478px on the web canvas; see the size note above.
                        // Inset pane ~10px (Figma ≈9.6); step = px/4 in BM.
                        'relative my-3.5 ml-3 hidden w-119.5 shrink-0 overflow-hidden md:block',
                        rounded ? 'rounded-2.5' : 'rounded-none',
                        paneClassName,
                    )}
                >
                    {resolvedPane}
                </div>
            ) : null}
            {/* `relative` anchors the scroll hint; the scrollbar is hidden, so
                without it nothing tells the reader there is more below. */}
            <div className="relative flex min-h-0 flex-1 flex-col">
                <div
                    ref={scroll.ref}
                    className={cn(
                        'scrollbar-hide flex min-h-0 flex-1 flex-col overflow-y-auto px-6 md:px-15.5',
                        bodyPadding === 'roomy' ? 'py-10' : 'py-7',
                        bodyClassName,
                    )}
                >
                    {children}
                    {/* Last inside the scroller, so "is this in view?" is the
                        same question as "has the reader reached the end?".
                        A pixel tall, not zero: at full scroll a zero-area box
                        sits exactly on the root's edge, and browsers do not
                        agree on whether that intersects. */}
                    <div
                        className="h-px"
                        ref={scroll.sentinelRef}
                        aria-hidden
                    />
                </div>
                <ScrollHint
                    show={scroll.showHint}
                    className={scrollHintClassName}
                />
            </div>
        </div>
    );
};
